import { type AdInputField, type AdInputLookup, getAt } from '@finn-no/mobility-item-insertion-shared';
import type { Locale } from '@finn-no/number-format';
import { i18n } from '@lingui/core';
import { TextField } from '@warp-ds/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { LeasingTerminationWidgetModel } from '../../../types/widget.js';
import { WidgetTypes } from '../../../types/widget-types.js';
import { SelectWidget } from './select-widget.js';
import { TextFieldWidget } from './textfield-widget.js';

type LeasingTerminationWidgetProps = Omit<LeasingTerminationWidgetModel, 'type'> & {
  adInputLookup: AdInputLookup;
  locale: Locale;
};

enum TerminationIds {
  DATE = 1,
  REMAINING_MONTHS = 2,
}

export const LeasingTerminationWidget = ({
  adInputLookup,
  helpText,
  locale,
  ...props
}: LeasingTerminationWidgetProps) => {
  const [terminationType, setTerminationType] = React.useState<TerminationIds>();
  const { control, getValues } = useFormContext();

  const dateInput = getAt<AdInputField>(adInputLookup, 'leasing_details.children.termination_date');
  const remainingMonthsInput = getAt<AdInputField>(adInputLookup, 'leasing_details.children.remaining_month');

  React.useEffect(() => {
    const [date, months] = getValues(['leasing_details.termination_date', 'leasing_details.remaining_month']);

    if (date && date !== '') {
      setTerminationType(TerminationIds.DATE);
    } else if (months && months !== '') {
      setTerminationType(TerminationIds.REMAINING_MONTHS);
    }
  }, []);

  // There is no specific field from ad-input-server for choosing termination type, so we need to create and maintain it ourselves, but it should not be part of our output when saving
  const options = [
    {
      id: TerminationIds.DATE,
      code: 'date',
      label: i18n.t({
        id: 'mobility-item-insertion.form.leasingTerminationWidget_dropdown.option.date',
        message: 'Date',
        comment:
          'Option for the dropdown that lets the user choose to specify the termination of the leasing agreement by a specific date.',
      }),
    },
    {
      id: TerminationIds.REMAINING_MONTHS,
      code: 'remaining_month',
      label: i18n.t({
        id: 'mobility-item-insertion.form.leasingTerminationWidget_dropdown.option.remaining_months',
        message: 'Remaining months',
        comment:
          'Option for the dropdown that lets the user choose to specify the termination of the leasing agreement by the number of remaining months.',
      }),
    },
  ];

  return (
    <>
      <SelectWidget
        label={i18n.t({
          id: 'mobility-item-insertion.form.leasingTerminationWidget_dropdown',
          message: 'Duration of the leasing agreement',
          comment:
            'Label for the dropdown that lets the user choose how they want to specify the duration of the leasing agreement.',
        })}
        helpText={helpText}
        values={options}
        value={terminationType?.toString()}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          setTerminationType(Number(e.target.value) as TerminationIds);
        }}
        mandatory={props.mandatory}
        name={'termination_type'}
        className="w-2/5"
      />
      {terminationType === TerminationIds.DATE && (
        <Controller
          name="leasing_details.termination_date"
          control={control}
          shouldUnregister={true} // It is important we unregister on unmount, otherwise the value will be submitted on save, even if this termination type is not chosen
          render={({ field }) => {
            return (
              <TextField {...field} label={dateInput?.label} type={'date' as never} className="mt-32 w-2/5" optional />
            );
          }}
        />
      )}
      {terminationType === TerminationIds.REMAINING_MONTHS && (
        <Controller
          name="leasing_details.remaining_month"
          control={control}
          shouldUnregister={true} // It is important we unregister on unmount, otherwise the value will be submitted on save, even if this termination type is not chosen
          render={({ field }) => {
            return (
              <TextFieldWidget
                {...field}
                type={WidgetTypes.NUMBER}
                label={remainingMonthsInput?.label}
                className="mt-32 w-2/5"
                optional
                formatAsNumber
                locale={locale}
              />
            );
          }}
        />
      )}
    </>
  );
};
